'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { DM_Sans, Instrument_Serif } from 'next/font/google'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'
import caseStudies from '@/data/case-studies-crm.json'

const dmSans = DM_Sans({ subsets: ['latin'], weight: ['400', '500', '700'] })
const instrumentSerif = Instrument_Serif({
  subsets: ['latin'],
  weight: ['400'],
  style: ['italic'],
})

const LOGO_SIZES = [30, 38, 50, 76, 50, 38, 30] as const

const HERO_LOGOS = [
  { src: '/images/zoho/books.svg', alt: 'Zoho Books' },
  { src: '/images/zoho/desk.svg', alt: 'Zoho Desk' },
  { src: '/images/zoho/flow.svg', alt: 'Zoho Flow' },
  { src: '/images/logo-zoho.png', alt: 'Zoho' },
  { src: '/images/zoho/crm.svg', alt: 'Zoho CRM' },
  { src: '/images/zoho/forms.svg', alt: 'Zoho Forms' },
  { src: '/images/zoho/analytics.svg', alt: 'Zoho Analytics' },
] as const

type CardVariant = 'peach' | 'lined' | 'mint' | 'butter'

type CaseStudy = {
  url: string
  category: string
  stat: string
  statLabel: string
  footer: string
  logo: { src: string; alt: string }
}

type CaseCard = CaseStudy & {
  id: string
  variant: CardVariant
  wave: string
}

const VARIANTS: CardVariant[] = ['peach', 'lined', 'mint', 'butter']
const WAVES = [
  'M0,45 Q50,35 100,40 Q150,15 200,20 Q240,25 280,30',
  'M0,40 Q70,20 140,30 T280,25',
  'M0,50 Q70,30 140,35 Q210,20 280,25',
  'M0,35 Q70,25 140,20 Q210,30 280,22',
] as const

const CARDS: CaseCard[] = (caseStudies as CaseStudy[]).map((item, index) => ({
  ...item,
  id: `zoho-case-${index}-${item.url.replace(/\W/g, '')}`,
  variant: VARIANTS[index % VARIANTS.length],
  wave: WAVES[index % WAVES.length],
}))

function CaseStudyCard({
  card,
  active,
}: {
  card: CaseCard
  active: boolean
}) {
  return (
    <article className={`case-scoop case-scoop--${card.variant}${active ? ' is-front' : ''} ${dmSans.className}`}>
      <span className={`case-scoop-badge${active ? ' is-active' : ''}`}>
        <svg className="case-scoop-badge-svg" viewBox="0 0 24 24" width="96" height="96" aria-hidden={true}>
          <defs>
            <clipPath id={`zoho-case-logo-${card.id}`}>
              <circle cx="12" cy="12" r="8" />
            </clipPath>
          </defs>
          <image
            href={card.logo.src}
            width="24"
            height="24"
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#zoho-case-logo-${card.id})`}
          />
        </svg>
      </span>
      <div className="case-scoop-inner">
        <div className="case-scoop-glow" aria-hidden={true} />
        <div className="case-scoop-head">
          <div className="case-scoop-card-title">{card.url}</div>
          <div className="case-scoop-card-cat">{card.category}</div>
        </div>
        <div className="case-scoop-metric">
          <div className="case-scoop-metric-value">
            {card.stat}
            <span className="case-scoop-arrow">↗</span>
          </div>
          <div className="case-scoop-metric-label">{card.statLabel}</div>
        </div>
        <div className="case-scoop-chart">
          <div className="case-scoop-wave">
            <svg width="280" height="60" viewBox="0 0 280 60" aria-hidden={true}>
              <path className="case-scoop-wave-path" d={card.wave} />
            </svg>
          </div>
        </div>
        <p className="case-scoop-blurb">{card.footer}</p>
      </div>
    </article>
  )
}

export default function ZohoHero() {
  const [active, setActive] = useState(0)
  const [hasCycled, setHasCycled] = useState(false)
  const [peek, setPeek] = useState(2)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 56.99rem)')
    const sync = () => setPeek(mq.matches ? 2 : 5)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  return (
    <section className="case-scoop-section case-scoop-section--hero" aria-label="Zoho CRM Rescue">
      <div className="case-scoop-hero-bg" aria-hidden={true}>
        <Image
          src="/images/services/zoho-hero.jpg"
          alt=""
          fill
          className="object-cover object-center"
          priority
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-[#2853a0a1]" />
        <div className="absolute inset-0 bg-[#071631]/55 sm:hidden" />
      </div>

      <header className={`flex items-center justify-center gap-3 px-4 pt-8 sm:gap-5 lg:pt-10 ${dmSans.className}`}>
        {LOGO_SIZES.map((size, index) => {
          const center = index === Math.floor(LOGO_SIZES.length / 2)
          const outer = index === 0 || index === LOGO_SIZES.length - 1
          const logo = HERO_LOGOS[index]
          return (
            <div
              key={`${size}-${index}`}
              className={`${outer ? 'hidden sm:flex' : 'flex'} items-center justify-center rounded-[22%] border border-white/35 bg-white shadow-[0_12px_35px_rgba(3,18,50,0.25)] ${
                center
                  ? 'ring-4 ring-white/25'
                  : 'opacity-70'
              }`}
              style={{ width: size, height: size }}
              aria-hidden={!center}
            >
              <Image
                src={logo.src}
                alt={center ? logo.alt : ''}
                width={size}
                height={size}
                className={`h-full w-full rounded-[22%] object-contain ${center ? 'p-[6%]' : 'p-[15%]'}`}
              />
            </div>
          )
        })}
      </header>

      <div className="case-scoop-layout">
        <div className={`case-scoop-copy ${dmSans.className}`}>
          <h1 className="case-scoop-heading">
            Zoho CRM - audited, fixed,
            <span className={`case-scoop-heading-em ${instrumentSerif.className}`}>
              connected, automated, measured.
            </span>
          </h1>
          <p className="case-scoop-sub">
            Most Zoho setups don&apos;t break. They drift. I go through what you have, fix what&apos;s wrong,
            connect it to the rest of your stack, and tie every lead back to revenue.
          </p>
          <div className="case-scoop-cta">
            <Link
              href="#plans"
              className={`hero-quote-cta ${dmSans.className}`}
              onClick={() => trackPostHogEvent('cta_click', {
                cta_text: 'View pricing',
                cta_location: 'zoho_hero',
                cta_type: 'primary',
              })}
            >
              <span className="hero-quote-cta__text">View pricing</span>
              <span className="hero-quote-cta__shimmer" aria-hidden={true} />
            </Link>
          </div>
          
        </div>

        <div className="case-scoop-right">
          <div className="case-scoop-deck">
            {CARDS.map((card, index) => {
              const stackPos = (index - active + CARDS.length) % CARDS.length
              const outgoing = hasCycled && stackPos === CARDS.length - 1
              if (stackPos >= peek && !outgoing) return null
              return (
                <div
                  key={card.id}
                  className="case-scoop-layer"
                  style={{
                    zIndex: outgoing ? CARDS.length + 1 : CARDS.length - stackPos,
                    opacity: outgoing ? 0 : 1,
                    pointerEvents: stackPos === 0 ? 'auto' : 'none',
                    transform: outgoing
                      ? 'translate(-48%, 0)'
                      : `translate(calc(var(--stack-x) * ${stackPos}), 0)`,
                  }}
                  aria-hidden={stackPos !== 0}
                >
                  <CaseStudyCard card={card} active={stackPos === 0} />
                </div>
              )
            })}
          </div>
          <button
            type="button"
            className="case-scoop-nav"
            aria-label="Next Zoho rescue stage"
            onClick={() => {
              setHasCycled(true)
              setActive((i) => (i + 1) % CARDS.length)
            }}
          >
            <svg className="[stroke-linecap:round] [stroke-linejoin:round]" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden={true}>
              <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2.4" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  )
}
